import React, { useState, ReactElement, useEffect, useCallback } from 'react';
import loading from '../../../../assets/loading.svg';
import { useParams } from 'react-router-dom';
import { Col, Layout, Modal, Row } from 'antd';
import axiosConfig, { api } from '../../../../utils/axiosConfig';
import EmailVerifyCards from './EmailVerifyCards';
import DomainsPanel from './DomainsPanel';
import { readWebsiteVariable } from '../../../../utils/readWebsiteVariable';
import { Sender } from '../../../../interfaces/api/Senders';
import { AxiosError } from 'axios';

const { Content } = Layout;

const EmailVerification = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const { accountId } = useParams<{ accountId: string }>();
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [domainModalVisible, setDomainModalVisible] = useState(false);
  const [domains, setDomains] = useState<Sender[]>([]);
  const [emails, setEmails] = useState<Sender[]>([]);
  const [submittingDomain, setSubmittingDomain] = useState(false);

  const fetchSenders = useCallback(async () => {
    const response = await api('GET', `/${accountId}/emailer/senders`);
    const data = response.data as Sender[];
    setDomains(data.filter((d) => d.type === 'domain'));
    setEmails(
      data.filter(
        (e) =>
          e.type === 'email' &&
          e.sender !== 'noreply@notificationapi.com' &&
          e.sender !== 'noreply@ipanoitacifiton.com'
      )
    );
  }, [accountId]);

  useEffect(() => {
    fetchSenders();
  }, [fetchSenders]);

  //ToDo: combine onSubmitDomain and onSubmitEmail
  const onSubmitDomain = async (sender: string): Promise<void> => {
    try {
      setSubmittingDomain(true);
      await api('post', `/${accountId}/emailer/senders`, { sender: sender });
      setDomainModalVisible(false);
      await fetchSenders();
    } catch (e) {
      const error = e as AxiosError;
      if (error.response?.data.message === 'duplicate sender') {
        Modal.error({
          title: 'Error',
          content:
            'This domain is already added to NotificationAPI. Contact support if you need to move it to your account.'
        });
      } else {
        Modal.error({
          title: 'Error',
          content:
            'An error occurred while verifying the domain. Our team has been automatically notified and will contact you as soon as possible.'
        });
        console.error(e);
      }
    }
    setSubmittingDomain(false);
  };
  const onRemoveSender = async (sender: string): Promise<void> => {
    setIsLoading(true);
    await axiosConfig(
      'delete',
      `/${accountId}/emailer/senders/${sender}`,
      {},
      () => {
        fetchSenders().then(() => setIsLoading(false));
      },
      (e) => {
        console.log(e);
        setIsLoading(false);
      }
    );
  };
  const onSubmitEmail = (sender: string): void => {
    setIsLoading(true);
    if (!emails.find((e) => e.sender === sender)) {
      axiosConfig(
        'post',
        `/${accountId}/emailer/senders`,
        { sender: sender },
        () => {
          fetchSenders().then(() => setIsLoading(false));
          setEmailModalVisible(false);
        },
        (e) => {
          console.log(e);
          setIsLoading(false);
        }
      );
    } else {
      setIsLoading(false);
    }
  };
  readWebsiteVariable('DOMAIN');
  return (
    <Content style={{ padding: '0 24px 24px 24px' }}>
      {isLoading ? (
        <div className="spinner">
          <img src={loading} alt="Loading" />
        </div>
      ) : (
        <></>
      )}
      <Row>
        <Col span={24} xxl={16}>
          <DomainsPanel
            modalVisible={domainModalVisible}
            setModalVisible={setDomainModalVisible}
            allDomains={domains}
            onRemoveSender={onRemoveSender}
            onSubmitDomain={onSubmitDomain}
            submittingDomain={submittingDomain}
          />
          {emails.length > 0 ? (
            <EmailVerifyCards
              modalVisible={emailModalVisible}
              setModalVisible={setEmailModalVisible}
              onSubmitEmail={onSubmitEmail}
              onRemoveSender={onRemoveSender}
              emails={emails}
            />
          ) : (
            <></>
          )}
        </Col>
      </Row>
    </Content>
  );
};
export default EmailVerification;
